import React, { useState } from 'react';
import { DollarSign, TrendingUp, Calculator, RefreshCw } from 'lucide-react';

const ROICalculatorNutra = () => {
  const [inputs, setInputs] = useState({
    monthlyRevenue: 100000,
    monthlyVisitors: 50000,
    currentConversionRate: 2.0,
    cartAbandonmentRate: 70,
    averageOrderValue: 65,
    supportAgents: 2,
    avgAgentSalary: 3500,
    monthlyTickets: 2000,
    repeatPurchaseRate: 20
  });

  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const resetToDefaults = () => {
    setInputs({
      monthlyRevenue: 100000,
      monthlyVisitors: 50000,
      currentConversionRate: 2.0,
      cartAbandonmentRate: 70,
      averageOrderValue: 65,
      supportAgents: 2,
      avgAgentSalary: 3500,
      monthlyTickets: 2000,
      repeatPurchaseRate: 20
    });
  };

  // Calculations
  const calculations = {
    // AI Impact Assumptions
    conversionRateIncrease: 0.5, // 0.5% point increase
    cartRecoveryRate: 18, // 18% of abandoned carts recovered
    supportCostReduction: 60, // 60% reduction
    aoVIncrease: 25, // 25% AOV increase
    repeatPurchaseIncrease: 35, // 35% increase in repeat rate
    automationRate: 75, // 75% of tickets automated
    
    // Current State
    get currentMonthlyCost() {
      return inputs.supportAgents * inputs.avgAgentSalary;
    },
    get currentAnnualRevenue() {
      return inputs.monthlyRevenue * 12;
    },
    
    // Revenue Improvements
    get newConversionRate() {
      return inputs.currentConversionRate + this.conversionRateIncrease;
    },
    get conversionRevenue() {
      return (inputs.monthlyVisitors * (this.newConversionRate / 100) * inputs.averageOrderValue) -
             (inputs.monthlyVisitors * (inputs.currentConversionRate / 100) * inputs.averageOrderValue);
    },
    get cartRecoveryRevenue() {
      const abandonedCarts = inputs.monthlyVisitors * (inputs.cartAbandonmentRate / 100);
      const recoveredCarts = abandonedCarts * (this.cartRecoveryRate / 100);
      return recoveredCarts * inputs.averageOrderValue;
    },
    get aoVRevenue() {
      const currentOrders = inputs.monthlyVisitors * (inputs.currentConversionRate / 100);
      return currentOrders * inputs.averageOrderValue * (this.aoVIncrease / 100);
    },
    get repeatPurchaseRevenue() {
      const currentOrders = inputs.monthlyVisitors * (inputs.currentConversionRate / 100);
      const newRepeatRate = inputs.repeatPurchaseRate * (1 + this.repeatPurchaseIncrease / 100);
      const additionalRepeats = currentOrders * ((newRepeatRate - inputs.repeatPurchaseRate) / 100);
      return additionalRepeats * inputs.averageOrderValue;
    },
    get totalMonthlyRevenueIncrease() {
      return this.conversionRevenue + this.cartRecoveryRevenue + this.aoVRevenue + this.repeatPurchaseRevenue;
    },
    get totalAnnualRevenueIncrease() {
      return this.totalMonthlyRevenueIncrease * 12;
    },
    
    // Cost Savings
    get supportCostSavings() {
      return this.currentMonthlyCost * (this.supportCostReduction / 100);
    },
    get automatedTickets() {
      return inputs.monthlyTickets * (this.automationRate / 100);
    },
    get timePerTicket() {
      return 10; // minutes
    },
    get hoursSaved() {
      return (this.automatedTickets * this.timePerTicket) / 60;
    },
    
    // AI Service Cost (estimated)
    get aiServiceCost() {
      if (inputs.monthlyRevenue < 50000) return 1500;
      if (inputs.monthlyRevenue < 100000) return 2000;
      if (inputs.monthlyRevenue < 250000) return 3000;
      if (inputs.monthlyRevenue < 500000) return 4500;
      return 6000;
    },
    
    // Net Benefit
    get monthlyNetBenefit() {
      return this.totalMonthlyRevenueIncrease + this.supportCostSavings - this.aiServiceCost;
    },
    get annualNetBenefit() {
      return this.monthlyNetBenefit * 12;
    },
    
    // ROI
    get annualInvestment() {
      return this.aiServiceCost * 12;
    },
    get roi() {
      return (this.annualNetBenefit / this.annualInvestment);
    },
    get paybackPeriod() {
      return this.aiServiceCost / this.monthlyNetBenefit;
    },
    
    // New State
    get newMonthlyRevenue() {
      return inputs.monthlyRevenue + this.totalMonthlyRevenueIncrease;
    },
    get newMonthlyCost() {
      return this.currentMonthlyCost - this.supportCostSavings + this.aiServiceCost;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 pt-24 pb-4 md:pb-8 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Calculator className="w-12 h-12 text-blue-600" />
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                AI Chatbot ROI Calculator
              </h1>
              <p className="text-gray-600">Calculate your return on investment for nutraceutical e-commerce</p>
            </div>
          </div>
          <button
            onClick={resetToDefaults}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Reset to Default Values
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-blue-600" />
                Your Current Metrics
              </h2>

              <div className="space-y-6">
                {/* Revenue Metrics */}
                <div>
                  <h3 className="font-bold text-lg text-gray-800 mb-3">Revenue Metrics</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monthly Revenue ($)
                      </label>
                      <input
                        type="number"
                        value={inputs.monthlyRevenue}
                        onChange={(e) => handleInputChange('monthlyRevenue', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none bg-white text-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monthly Website Visitors
                      </label>
                      <input
                        type="number"
                        value={inputs.monthlyVisitors}
                        onChange={(e) => handleInputChange('monthlyVisitors', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none bg-white text-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Conversion Rate (%)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={inputs.currentConversionRate}
                        onChange={(e) => handleInputChange('currentConversionRate', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none bg-white text-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cart Abandonment Rate (%)
                      </label>
                      <input
                        type="number"
                        value={inputs.cartAbandonmentRate}
                        onChange={(e) => handleInputChange('cartAbandonmentRate', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none bg-white text-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Average Order Value ($)
                      </label>
                      <input
                        type="number"
                        value={inputs.averageOrderValue}
                        onChange={(e) => handleInputChange('averageOrderValue', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none bg-white text-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Repeat Purchase Rate (%)
                      </label>
                      <input
                        type="number"
                        value={inputs.repeatPurchaseRate}
                        onChange={(e) => handleInputChange('repeatPurchaseRate', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none bg-white text-gray-900"
                      />
                    </div>
                  </div>
                </div>

                {/* Support Metrics */}
                <div>
                  <h3 className="font-bold text-lg text-gray-800 mb-3">Support Metrics</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Support Agents
                      </label>
                      <input
                        type="number"
                        value={inputs.supportAgents}
                        onChange={(e) => handleInputChange('supportAgents', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none bg-white text-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Average Agent Salary/Month ($)
                      </label>
                      <input
                        type="number"
                        value={inputs.avgAgentSalary}
                        onChange={(e) => handleInputChange('avgAgentSalary', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none bg-white text-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monthly Support Tickets
                      </label>
                      <input
                        type="number"
                        value={inputs.monthlyTickets}
                        onChange={(e) => handleInputChange('monthlyTickets', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none bg-white text-gray-900"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white p-6 rounded-xl shadow-lg">
                <div className="text-sm mb-1 opacity-90">Monthly Net Benefit</div>
                <div className="text-3xl font-bold">
                  ${calculations.monthlyNetBenefit.toLocaleString(undefined, {maximumFractionDigits: 0})}
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white p-6 rounded-xl shadow-lg">
                <div className="text-sm mb-1 opacity-90">Annual ROI</div>
                <div className="text-3xl font-bold">
                  {calculations.roi.toFixed(0)}x
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-pink-600 text-white p-6 rounded-xl shadow-lg">
                <div className="text-sm mb-1 opacity-90">Payback Period</div>
                <div className="text-3xl font-bold">
                  {calculations.paybackPeriod.toFixed(1)} months
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-500 to-red-600 text-white p-6 rounded-xl shadow-lg">
                <div className="text-sm mb-1 opacity-90">Annual Benefit</div>
                <div className="text-3xl font-bold">
                  ${calculations.annualNetBenefit.toLocaleString(undefined, {maximumFractionDigits: 0})}
                </div>
              </div>
            </div>

            {/* Revenue Breakdown */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Revenue Impact Breakdown</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="font-medium text-gray-700">Improved Conversion Rate</span>
                  <span className="font-bold text-green-600">
                    +${calculations.conversionRevenue.toLocaleString(undefined, {maximumFractionDigits: 0})}/mo
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="font-medium text-gray-700">Cart Abandonment Recovery</span>
                  <span className="font-bold text-green-600">
                    +${calculations.cartRecoveryRevenue.toLocaleString(undefined, {maximumFractionDigits: 0})}/mo
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="font-medium text-gray-700">Average Order Value Increase</span>
                  <span className="font-bold text-green-600">
                    +${calculations.aoVRevenue.toLocaleString(undefined, {maximumFractionDigits: 0})}/mo
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="font-medium text-gray-700">Repeat Purchase Improvement</span>
                  <span className="font-bold text-green-600">
                    +${calculations.repeatPurchaseRevenue.toLocaleString(undefined, {maximumFractionDigits: 0})}/mo
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-100 rounded-lg border-2 border-green-300">
                  <span className="font-bold text-gray-900">Total Revenue Increase</span>
                  <span className="font-bold text-green-600 text-lg">
                    +${calculations.totalMonthlyRevenueIncrease.toLocaleString(undefined, {maximumFractionDigits: 0})}/mo
                  </span>
                </div>
              </div>
            </div>

            {/* Cost Analysis */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Cost Analysis</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="font-medium text-gray-700">Current Support Costs</span>
                  <span className="font-bold text-gray-900">
                    ${calculations.currentMonthlyCost.toLocaleString()}/mo
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="font-medium text-gray-700">Support Cost Savings (60%)</span>
                  <span className="font-bold text-green-600">
                    -${calculations.supportCostSavings.toLocaleString()}/mo
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="font-medium text-gray-700">AI Service Cost</span>
                  <span className="font-bold text-red-600">
                    -${calculations.aiServiceCost.toLocaleString()}/mo
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-100 rounded-lg border-2 border-blue-300">
                  <span className="font-bold text-gray-900">New Monthly Cost</span>
                  <span className="font-bold text-blue-600 text-lg">
                    ${calculations.newMonthlyCost.toLocaleString()}/mo
                  </span>
                </div>
              </div>

              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-2">Efficiency Metrics</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-gray-500">Tickets Automated</div>
                    <div className="text-lg font-bold text-gray-900">
                      {calculations.automatedTickets.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Hours Saved</div>
                    <div className="text-lg font-bold text-gray-900">
                      {calculations.hoursSaved.toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Before & After Comparison */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Before & After Comparison</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600 mb-3 font-medium">BEFORE AI</div>
                  <div className="space-y-2">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <div className="text-xs text-gray-600">Monthly Revenue</div>
                      <div className="font-bold text-gray-900">
                        ${inputs.monthlyRevenue.toLocaleString()}
                      </div>
                    </div>
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <div className="text-xs text-gray-600">Conversion Rate</div>
                      <div className="font-bold text-gray-900">
                        {inputs.currentConversionRate}%
                      </div>
                    </div>
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <div className="text-xs text-gray-600">Support Costs</div>
                      <div className="font-bold text-gray-900">
                        ${calculations.currentMonthlyCost.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-green-600 mb-3 font-medium">AFTER AI</div>
                  <div className="space-y-2">
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="text-xs text-green-700">Monthly Revenue</div>
                      <div className="font-bold text-green-900">
                        ${calculations.newMonthlyRevenue.toLocaleString(undefined, {maximumFractionDigits: 0})}
                      </div>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="text-xs text-green-700">Conversion Rate</div>
                      <div className="font-bold text-green-900">
                        {calculations.newConversionRate}%
                      </div>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="text-xs text-green-700">Total Costs</div>
                      <div className="font-bold text-green-900">
                        ${calculations.newMonthlyCost.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Statement */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-3">Executive Summary</h3>
              <p className="text-blue-100 mb-4">
                By implementing AI chatbots and messaging automation, your business can expect:
              </p>
              <ul className="space-y-2 text-blue-50">
                <li className="flex items-start gap-2">
                  <span className="text-yellow-300 font-bold">•</span>
                  <span>
                    <strong className="text-white">
                      ${calculations.totalMonthlyRevenueIncrease.toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </strong> additional monthly revenue ({((calculations.totalMonthlyRevenueIncrease / inputs.monthlyRevenue) * 100).toFixed(1)}% increase)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-300 font-bold">•</span>
                  <span>
                    <strong className="text-white">
                      ${calculations.supportCostSavings.toLocaleString()}
                    </strong> monthly savings in support costs
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-300 font-bold">•</span>
                  <span>
                    Net monthly benefit of <strong className="text-white">
                      ${calculations.monthlyNetBenefit.toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </strong> after AI service costs
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-300 font-bold">•</span>
                  <span>
                    ROI of <strong className="text-white">{calculations.roi.toFixed(0)}x</strong> with payback in just <strong className="text-white">{calculations.paybackPeriod.toFixed(1)} months</strong>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Assumptions Section */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-blue-600" />
            Calculation Assumptions
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-bold text-gray-800 mb-3">Revenue Impact</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Conversion rate: +0.5% points</li>
                <li>• Cart recovery: 18% of abandoned</li>
                <li>• AOV increase: +25%</li>
                <li>• Repeat purchase: +35%</li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-gray-800 mb-3">Cost Savings</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Support cost reduction: 60%</li>
                <li>• Automation rate: 75% of tickets</li>
                <li>• Time per ticket: 10 minutes</li>
                <li>• No additional hiring needed</li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-gray-800 mb-3">Conservative Estimates</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Based on industry benchmarks</li>
                <li>• Results may vary by business</li>
                <li>• Continuous optimization included</li>
                <li>• 90-day ramp-up period assumed</li>
              </ul>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-8 bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-xl shadow-xl p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Achieve These Results?</h2>
          <p className="text-green-100 text-lg mb-6 max-w-2xl mx-auto">
            Schedule a free consultation to see how we can customize these solutions for your specific business
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-white text-green-600 px-8 py-3 rounded-lg font-bold hover:bg-green-50 transition-colors shadow-lg">
              Schedule Demo
            </button>
            <button className="bg-green-700 text-white px-8 py-3 rounded-lg font-bold hover:bg-green-800 transition-colors border-2 border-white">
              Download Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROICalculatorNutra;

